import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, Power, PowerOff, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { listChecks, updateCheck, type CheckPayload } from "@/api/checks";
import { createDraftCheck } from "@/api/authoring";
import type { CheckSummary } from "@/api/types";
import { Page } from "@/components/page";
import { EmptyState } from "@/components/empty-state";

export function ChecksListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const checks = useQuery({
    queryKey: ["checks"],
    queryFn: listChecks,
  });

  const toggle = useMutation({
    mutationFn: async (c: CheckSummary) => {
      // The PUT endpoint takes the full doc — fetch it via the cached list
      // and flip enabled.
      // The PUT replaces the whole doc, so carry every editable field
      // through — omitting them would null shop_system_label /
      // llm_verification_prompt / brief on a mere enable/disable toggle.
      const payload: CheckPayload = {
        name: c.name,
        priority: c.priority,
        action_id: c.action.id,
        enabled: !c.enabled,
        shop_system_label: c.shop_system_label,
        llm_verification_prompt: c.llm_verification_prompt,
        brief: c.brief,
        kind: c.kind,
        positive_rules: c.positive_rules,
        negative_rules: c.negative_rules,
      };
      return updateCheck(c.id, payload);
    },
    onSuccess: (updated) => {
      toast.success(`${updated.name}: ${updated.enabled ? "enabled" : "disabled"}`);
      qc.invalidateQueries({ queryKey: ["checks"] });
    },
  });

  const generate = useMutation({
    mutationFn: createDraftCheck,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["checks"] });
      navigate({ to: "/checks/$checkId", params: { checkId: r.id } });
    },
    onError: () => toast.error("Could not create a draft check."),
  });

  const items = checks.data ?? [];

  return (
    <Page
      title="Checks"
      actions={
        <div className="flex items-center gap-2">
          <button
            className="btn-ghost text-sm"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            title="Create a draft check and improve it with the AI agent"
          >
            <Sparkles className="h-4 w-4" /> Generate with AI
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate({ to: "/checks/$checkId", params: { checkId: "new" } })}
          >
            <Plus className="h-4 w-4" /> New check
          </button>
        </div>
      }
    >
      <section className="card overflow-hidden">
        {items.length === 0 && !checks.isLoading ? (
          <EmptyState title="No checks yet" hint="Create one to start tagging domains." />
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="text-xs text-muted">
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-right font-medium">Priority</th>
                <th className="px-4 py-2 text-left font-medium">Action</th>
                <th className="px-4 py-2 text-right font-medium">Version</th>
                <th className="px-4 py-2 text-left font-medium">Enabled</th>
                <th className="px-4 py-2 text-right font-medium">Rules</th>
                <th
                  className="px-4 py-2 text-right font-medium"
                  title="Share of primary matches the LLM verifier overruled (rule's false-positive rate). Lower = rule fires more accurately."
                >
                  LLM rejected
                </th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-subtle/40">
                  <td className="px-4 py-2">
                    <Link
                      to="/checks/$checkId"
                      params={{ checkId: c.id }}
                      className="text-accent hover:underline"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{c.priority}</td>
                  <td className="px-4 py-2">
                    <span className="badge text-[10px]">
                      {c.action.name}
                      <span className="ml-1 text-muted">({c.action.spec.kind})</span>
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted">v{c.version}</td>
                  <td className="px-4 py-2">
                    {c.enabled ? (
                      <span className="badge-success text-[10px]">on</span>
                    ) : (
                      <span className="badge text-[10px] text-muted">off</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted">
                    +{c.positive_rules.length} / −{c.negative_rules.length}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted">
                    {c.verifier_stats ? (
                      <span
                        title={
                          `LLM rejected ${c.verifier_stats.fp} of ` +
                          `${c.verifier_stats.total} primary matches ` +
                          `(${c.verifier_stats.tp} kept). Lower = rule fires more accurately.`
                        }
                      >
                        {c.verifier_stats.fp} / {c.verifier_stats.total}
                        {c.verifier_stats.total > 0 && (
                          <span className="ml-1 text-[10px]">
                            ({Math.round((100 * c.verifier_stats.fp) / c.verifier_stats.total)}%)
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="btn-ghost text-xs"
                      onClick={() => toggle.mutate(c)}
                      disabled={toggle.isPending}
                      title={c.enabled ? "Disable" : "Enable"}
                    >
                      {c.enabled ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </Page>
  );
}
